import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "../styles/navbar.css";
import ScrambledText from "./animations/scrambledText/scrambledText.js";
import { useLocation, useNavigate } from "react-router-dom";
import ysIcon from "../assets/images/ys.svg";

// -------------------------------
// MOBILE DETECTION HOOK
// -------------------------------
function useIsMobile(breakpoint = 765) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
}

// -------------------------------
// MAIN NAVBAR COMPONENT
// -------------------------------
export default function Navbar({ lang, setLang, showMobileNav }) {
  const isMobile = useIsMobile(1000);
  const location = useLocation();
  const isContactPage = location.pathname === "/contactYoru";

  return (
    <>
      {isMobile ? (
        <MobileNavbar
          lang={lang}
          setLang={setLang}
          isContactPage={isContactPage}
          showMobileNav={showMobileNav}
        />
      ) : (
        <DesktopNavbar
          lang={lang}
          setLang={setLang}
          isContactPage={isContactPage}
        />
      )}
    </>
  );
}

// -------------------------------
// DESKTOP NAVBAR
// -------------------------------
function DesktopNavbar({ lang, setLang, isContactPage }) {
  const [contactClicked, setContactClicked] = useState(false);
  const [homeClicked, setHomeClicked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isPortfolioPage = location.pathname === "/portfolio";
  const isAboutPage = location.pathname === "/about-yoru";
  // eslint-disable-next-line no-unused-vars
  const isHomePage = location.pathname === "/";
  // Scroll to top when portfolio page loads
  useEffect(() => {
    if (isPortfolioPage) {
      window.scrollTo(0, 0);
    }
  }, [isPortfolioPage]);

  const labels = {
    EN: {
      contact: isContactPage ? "HOME" : "CONTACT US",
      about: "ABOUT",
      portfolio: "PORTFOLIO",
      home: "HOME",
    },
    FR: {
      contact: isContactPage ? "ACCUEIL" : "CONTACT",
      about: "À PROPOS",
      portfolio: "PORTFOLIO",
      home: "ACCUEIL",
    },
  };

  const currentLabels = labels[lang];

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const handleContactClick = () => {
    setContactClicked(true);
    if (isContactPage) {
      navigate("/#Header");
    } else {
      navigate("/contactYoru");
    }
    setTimeout(() => setContactClicked(false), 400);
    scrollToTop();
  };

  const handleHomeClick = () => {
    setHomeClicked(true);
    if (location.pathname === "/") {
      const headerElement = document.getElementById("Header");
      if (headerElement) {
        headerElement.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/#Header");
    }
    scrollToTop();
    setTimeout(() => setHomeClicked(false), 400);
  };

  const handleAboutClick = () => {
    navigate("/about-yoru");
    scrollToTop();
  };

  const handlePortfolioClick = () => {
    navigate("/portfolio");
    scrollToTop();
  };

  return (
    <section className="navbar-container">
      <div className="left-menu">
        {/* CONTACT/HOME BUTTON - Changes based on current page */}
        <div
          className="navbar-btn"
          onClick={handleContactClick}
          style={{ cursor: "pointer" }}
        >
          <ScrambledText
            radius={100}
            duration={1.2}
            speed={0.5}
            scrambleChars=".:"
          >
            {currentLabels.contact}
          </ScrambledText>

          <i
            className={`fa-solid fa-arrow-right ${
              contactClicked ? "rotate-up" : ""
            }`}
          ></i>
        </div>

        {/* ABOUT/HOME BUTTON - Changes based on current page */}
          <div className="navbar-btn" style={{ cursor: "pointer" }}>
            {isAboutPage ? (
              // On About page, show Home button
              <div onClick={handleHomeClick} className="nav-link-button">
                <ScrambledText
                  radius={100}
                  duration={1.2}
                  speed={0.5}
                  scrambleChars=".:"
                >
                  {currentLabels.home}
                </ScrambledText>

                <i
                  className={`fa-solid fa-arrow-down ${
                    homeClicked ? "rotate-up" : ""
                  }`}
                ></i>
              </div>
            ) : (
              // Not on About page, show About button
              <div onClick={handleAboutClick} className="nav-link-button">
                <ScrambledText
                  radius={100}
                  duration={1.2}
                  speed={0.5}
                  scrambleChars=".:"
                >
                  {currentLabels.about}
                </ScrambledText>

                <i className="fa-solid fa-arrow-down"></i>
              </div>
            )}
          </div>
      </div>

      {/* LANGUAGE BUTTON */}
      <button
        className="lang-toggle"
        onClick={() => {
          if (typeof setLang === "function") {
            setLang(lang === "EN" ? "FR" : "EN");
          }
        }}
      >
        {lang === "EN" ? "FR" : "EN"}
      </button>

      {/* PORTFOLIO / HOME LINK */}
      { isPortfolioPage ? (
        <div onClick={handleHomeClick} className="navbar-btn portfolio-link">
          <ScrambledText
            radius={100}
            duration={1.2}
            speed={0.5}
            scrambleChars=".:"
          >
            {currentLabels.home}
          </ScrambledText>

          <div className="icon-container">
            <img src={ysIcon} alt="Yoru Solutions" className="navbar-icon" width="32" height="30" />
          </div>
        </div>
      ) : (
        <div
          onClick={handlePortfolioClick}
          className="navbar-btn portfolio-link"
        >
          <ScrambledText
            radius={100}
            duration={1.2}
            speed={0.5}
            scrambleChars=".:"
          >
            {currentLabels.portfolio}
          </ScrambledText>

          <div className="icon-container">
            <img src={ysIcon} alt="Yoru Solutions" className="navbar-icon" width="32" height="30" />
          </div>
        </div>
      )}
    </section>
  );
}

// -------------------------------
// MOBILE NAVBAR
// -------------------------------
function MobileNavbar({ lang, setLang, isContactPage, showMobileNav }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  // On homepage, only show when caller signals bottom of reveal-section reached
  if (isHomePage && !showMobileNav) return null;

  const labels = {
    EN: {
      home: "Home",
      about: "About",
      contact: "Contact",
      portfolio: "Portfolio",
    },
    FR: {
      home: "Accueil",
      about: "À Propos",
      contact: "Contact",
      portfolio: "Portfolio",
    },
  };

  const currentLabels = labels[lang];

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const handleItemClick = (to) => {
    if (isContactPage && to === "/contactYoru") {
      navigate("/");
    } else {
      navigate(to);
    }
    setTimeout(() => {
     scrollToTop();
   }, 100);
  };

  // Always show all items
  const items = [
    {
      to: "/",
      label: currentLabels.home,
      icon: ysIcon,
      isIconImage: true,
      activeIcon: "fa-solid fa-house",
      isActive: location.pathname === "/",
    },
    {
      to: "/about-yoru",
      label: currentLabels.about,
      icon: "fa-solid fa-star",
      activeIcon: "fa-solid fa-star",
      isActive: location.pathname === "/about-yoru",
    },
    {
      to: "/contactYoru",
      label: currentLabels.contact,
      icon: "fa-solid fa-phone",
      activeIcon: "fa-solid fa-phone",
      isActive: location.pathname === "/contactYoru",
    },
    {
      to: "/portfolio",
      label: currentLabels.portfolio,
      icon: "fa-solid fa-book",
      activeIcon: "fa-solid fa-book",
      isActive: location.pathname === "/portfolio",
    },
  ];

  const navMarkup = (
    <nav className="mobile-nav">
      {items.map((item, index) => (
        <div
          key={index}
          onClick={() => handleItemClick(item.to)}
          className={`mobile-nav-item ${item.isActive ? "active" : ""}`}
        >
          <div className="mobile-nav-icon-container">
            {item.isIconImage ? (
              <img
                src={item.icon}
                alt={item.label}
                className="mobile-nav-icon"
                width="24"
                height="24"
              />
            ) : (
              <i className={item.icon}></i>
            )}
          </div>
          <span className="mobile-nav-label">
            {item.label}
          </span>
        </div>
      ))}
      {typeof setLang === "function" && (
        <div
          className="mobile-nav-item"
          onClick={() => setLang(lang === "EN" ? "FR" : "EN")}
        >
          <div className="mobile-nav-icon-container">
            <i className="fa-solid fa-globe"></i>
          </div>
          <span className="mobile-nav-label">
            {lang === "EN" ? "FR" : "EN"}
          </span>
        </div>
      )}
    </nav>
  );

  // Render to body to escape any ancestor transforms/clipping on pages like Home
  return createPortal(navMarkup, document.body);
}
